import { Box, Grid } from '@mui/material';
import { BillingMetrics, BillingSubscription } from '../../../../types/admin';
import RefreshButton from '../../system/shared/RefreshButton';
import AdminMetricCard from '../../shared/AdminMetricCard';
import SubscriptionsTable from './SubscriptionsTable';
import { formatNumber } from '../../../../utils/formatters';

interface SubscriptionsOverviewTabProps {
  metrics: BillingMetrics;
  subscriptions: BillingSubscription[];
  loading?: boolean;
  lastUpdated: string;
  onRefresh: () => void;
  onCancelSubscription: (subscriptionId: string, reason?: string) => Promise<{ success: boolean }>;
}

export default function SubscriptionsOverviewTab({
  metrics,
  subscriptions,
  loading,
  lastUpdated,
  onRefresh,
  onCancelSubscription,
}: SubscriptionsOverviewTabProps) {
  return (
    <Box>
      {/* Header with Refresh Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <RefreshButton onRefresh={onRefresh} lastUpdated={lastUpdated} loading={loading} />
      </Box>

      {/* Subscription Metrics */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <AdminMetricCard
            title="Active Subscriptions"
            value={formatNumber(metrics.active_subscriptions)}
            trend={{
              value: Math.abs(metrics.active_subscriptions_change),
              label: `${metrics.active_subscriptions_change > 0 ? '+' : ''}${metrics.active_subscriptions_change}%`,
              direction: metrics.active_subscriptions_change >= 0 ? 'up' : 'down',
            }}
            subtitle={`${formatNumber(metrics.trial_subscriptions)} in trial`}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AdminMetricCard
            title="New MRR"
            value={`$${formatNumber(metrics.new_mrr)}`}
            trend={{
              value: Math.abs(metrics.new_mrr_change),
              label: `${metrics.new_mrr_change > 0 ? '+' : ''}${metrics.new_mrr_change}%`,
              direction: metrics.new_mrr_change >= 0 ? 'up' : 'down',
            }}
            subtitle="This month"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AdminMetricCard
            title="Churned MRR"
            value={`$${formatNumber(metrics.churned_mrr)}`}
            trend={{
              value: Math.abs(metrics.churned_mrr_change),
              label: `${metrics.churned_mrr_change > 0 ? '+' : ''}${metrics.churned_mrr_change}%`,
              direction: metrics.churned_mrr_change >= 0 ? 'up' : 'down',
            }}
            subtitle={`${formatNumber(metrics.canceled_this_month)} cancellations`}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AdminMetricCard
            title="Churn Rate"
            value={`${metrics.churn_rate}%`}
            trend={{
              value: Math.abs(metrics.churn_rate_change),
              label: `${metrics.churn_rate_change > 0 ? '+' : ''}${metrics.churn_rate_change}%`,
              direction: metrics.churn_rate_change >= 0 ? 'up' : 'down',
            }}
            subtitle="Monthly churn"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Subscriptions Table */}
      <Box sx={{ mt: 4 }}>
        <SubscriptionsTable
          subscriptions={subscriptions}
          loading={loading}
          onCancelSubscription={onCancelSubscription}
        />
      </Box>
    </Box>
  );
}
